import React, { memo, useMemo, useRef } from 'react';
import {
  MapContainer as LeafletMap,
  TileLayer,
  Marker,
  Popup,
} from 'react-leaflet';
import L from 'leaflet'; // Import Leaflet library
import { useMap } from 'react-leaflet';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

// Define a custom icon for the marker
const customIcon = new L.Icon({
  iconUrl: `${window.location.origin}/images/marker.svg`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Funkcia na automatické prispôsobenie mapy podľa markerov
function FitBounds({ markerData }) {
  const map = useMap();

  React.useEffect(() => {
    const fitToMarkers = () => {
      map.invalidateSize({ pan: false });

      const bounds = markerData.map((marker) => [
        marker.latitude,
        marker.longitude,
      ]);

      if (bounds.length === 0) {
        return;
      }

      if (bounds.length === 1) {
        map.setView(bounds[0], 15);
        return;
      }

      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 18 });
    };

    requestAnimationFrame(fitToMarkers);
  }, [map, markerData]);

  return null;
}

function SyncMapWithContainerSize() {
  const map = useMap();
  const currentViewRef = useRef({
    center: map.getCenter(),
    zoom: map.getZoom(),
  });

  React.useEffect(() => {
    const container = map.getContainer();

    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const rememberCurrentView = () => {
      currentViewRef.current = {
        center: map.getCenter(),
        zoom: map.getZoom(),
      };
    };

    const restoreMapSizeAndView = () => {
      const { width, height } = container.getBoundingClientRect();

      if (width === 0 || height === 0) {
        return;
      }

      const { center, zoom } = currentViewRef.current;

      map.invalidateSize({ pan: false });
      map.setView(center, zoom, { animate: false });
    };

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(restoreMapSizeAndView);
      window.setTimeout(restoreMapSizeAndView, 250);
    });

    map.on('moveend', rememberCurrentView);
    map.on('zoomend', rememberCurrentView);
    resizeObserver.observe(container);

    return () => {
      map.off('moveend', rememberCurrentView);
      map.off('zoomend', rememberCurrentView);
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
}

function OpenStreetMap(props) {
  const { t } = useTranslation();
  const {
    center = [49.2, 18.76],
    zoom = 15,
    maxZoom = 20,
    markerData = [
      {
        latitude: 49.202062,
        longitude: 18.761931,
        name: t('FRI UNIZA'),
      },
    ],
    buttonAction = null,
    buttonText = t('Náhľad'),
    buttonType = 'primary',
    ...restProps
  } = props;

  const normalizedMarkers = useMemo(() => {
    const toNumber = (value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        return Number.parseFloat(value.replace(',', '.'));
      }
      return Number.NaN;
    };

    return (markerData || [])
      .map((marker) => {
        const latitude = toNumber(marker?.latitude);
        const longitude = toNumber(marker?.longitude);
        return {
          ...marker,
          latitude,
          longitude,
        };
      })
      .filter(
        (marker) =>
          Number.isFinite(marker.latitude) &&
          Number.isFinite(marker.longitude) &&
          marker.latitude >= -90 &&
          marker.latitude <= 90 &&
          marker.longitude >= -180 &&
          marker.longitude <= 180 &&
          !(marker.latitude === 0 && marker.longitude === 0),
      );
  }, [markerData]);

  const markersToRender =
    normalizedMarkers.length > 0
      ? normalizedMarkers
      : [
          {
            latitude: center[0],
            longitude: center[1],
            name: t('FRI UNIZA'),
          },
        ];

  return (
    <LeafletMap
      center={center}
      zoom={zoom}
      maxZoom={maxZoom}
      attributionControl={true}
      zoomControl={true}
      doubleClickZoom={true}
      scrollWheelZoom={true}
      dragging={true}
      animate={true}
      easeLinearity={0.35}
      {...restProps}
    >
      <TileLayer url={'http://{s}.tile.osm.org/{z}/{x}/{y}.png'} />
      {markersToRender.map((marker, index) => (
        <Marker
          key={index}
          icon={customIcon}
          position={[marker.latitude, marker.longitude]}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '0px' }}>{marker.name}</p>
              {buttonAction && (
                <Button
                  type={buttonType}
                  onClick={() => buttonAction(marker)}
                  style={{ display: 'block', margin: '0 auto' }}
                >
                  {buttonText}
                </Button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
      <FitBounds markerData={markersToRender} />
      <SyncMapWithContainerSize />
    </LeafletMap>
  );
}

export default memo(OpenStreetMap);
